import { useState } from "react";
import { mutate } from "swr";
import { Loader2 } from "lucide-react";

import { OrganizationService, type OrgMember } from "@/services/organization.service";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RemoveMemberDialogProps {
  member: OrgMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RemoveMemberDialog({ member, open, onOpenChange }: RemoveMemberDialogProps) {
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRemove = async () => {
    if (!member) return;
    setIsRemoving(true);
    setError(null);
    try {
      await OrganizationService.removeMember(member.id);
      mutate("organization-members");
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message || "Failed to remove the member. Please try again.");
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove member</DialogTitle>
          <DialogDescription>
            {member
              ? `${member.user.name || member.user.email} will immediately lose access to this organization and be signed out everywhere. You can invite them again later.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleRemove} disabled={isRemoving || !member}>
            {isRemoving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Remove member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
